import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import _ from '@lodash';
import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { openDialog, closeDialog as fuseCloseDialog } from "app/store/fuse/dialogSlice";
import history from '@history';
import { useReactToPrint } from 'react-to-print';
import { yupResolver } from '@hookform/resolvers/yup';
import { updateCustomer } from 'app/main/apps/client/store/customerSlice';
import { closeViewOrderDialog, confirmInvoice, updateInvoice } from 'app/main/dashboards/main/store/widgetsSlice';
import { showMessage } from 'app/store/fuse/messageSlice';
import { getApiErrorMessage } from 'app/utils/apiError';
import { checkNullOrUndefined, isAdminOrStaff, isCustomer, isSupplier } from 'app/main/apps/common/AuraFunctions';
import FuseUtils from '@fuse/utils/FuseUtils';
import authPermissions from 'app/auth/authPermissions';
import { editShipment, getLastBoxNumber, openContainerDialog, updateShipment } from 'app/main/apps/backoffice/store/shipmentOrderSlice';
import { checkUniqueOrderNo } from 'app/main/apps/client/store/employeeSlice';
import { Loader } from 'app/main/apps/common/ListInfoMessage';
import ShipmentFormContent from 'app/main/apps/backoffice/shipments/dialogs/ShipmentFormContent';
import moment from 'moment';
import ContainerDialog from 'app/main/apps/backoffice/shipments/dialogs/ContainerDialog';
import AuraBaseDialog from '../../../common/AuraBaseDialog';
import OrderHeader from '../../../common/OrderHeader';
import { getOrderStatuses, getOrders } from '../../store/ordersSlice';
import { updateAddress } from '../../../settings/store/addressSlice';
import StatusFormContent from './StatusFormContent';
import {
  closeOrderDialog,
  getOrderDetails,
  resetOrder,
  performAction,
  updateOrder,
  newOrder,
  updateShipmentApproval,
  closeViewOrderDialog as orderCloseViewOrderDialog,
  addAdvancePayment,
  addComments,
  getOrder,
} from '../../store/orderSlice';
import NoteFormContent from './NoteFormContent';
import AddressFormContent from '../../../common/FormContent/AddressFormContent';
import getInitValues, { getSchema } from './OrderBase';
import PaymentFormContent from '../../../common/FormContent/PaymentFormContent';
import { getCategoryValues } from '../../../settings/store/configurationsSlice';
import InvoiceTab from '../tabs/InvoiceTab';
import DiscountFormContent from './DiscountFormContent';
import ShippingLabelContent from './ShippingLabelContent';
import CompleteShipmentFormContent from './CompleteShipmentFormContent';
import ConfirmInvoice from './ConfirmInvoice';
import UpdateInvoice from './UpdateInvoice';
import ReviewInvoiceDiscrepancy from './ReviewInvoiceDiscrepancy';
import InvoiceDiscrepancyPanel from './InvoiceDiscrepancyPanel';
import GenerateInvoice from './GenerateInvoice';
import QuickOrderEdit from './QuickOrderEdit';
import ViewNotes from './ViewNotes';
import RevisionHistories from '../tabs/RevisionHistories';
import InternalNotes from './InternalNotes';
import OpenContainersContent from './OpenContainersContent';
import QuickEditShipmentForm from './QuickEditShipmentForm';
import UpdateTrackingFormContent from './UpdateTrackingFormContent';

const ComponentToPrint = forwardRef((props, ref) => {
  return (<div ref={ref}>
    <InvoiceTab {...props} />
  </div>)
});

const PrintShippingLabelContent = forwardRef((props, ref) => {
  return (<div ref={ref}>
    <ShippingLabelContent {...props} />
  </div>)
});

import completeShipmentMessage from './completeShipmentMessage';
import NotLoadedBoxesDialog, { notLoadedScanPath, takenOutMessage, withCompleteAnyway, withTakeOut } from './NotLoadedBoxesDialog';

function OrderDialog(props) {
  const dispatch = useDispatch();
  const currentUser = useSelector(({ user }) => user);
  const orderDialog = useSelector(({ mainDashboardApp, eCommerceApp }) => {
    if (props.isDashboard) {
      return mainDashboardApp?.widgets?.orderDialog;
    }
    return eCommerceApp?.order?.orderDialog;
  });
  const { completeOrderData, data: paymentData } = orderDialog;
  const updateStatusValues = useSelector(({ eCommerceApp }) => eCommerceApp?.orders?.updateStatusValues);
  const [options, setOptions] = useState([]);
  const tempSchema = getSchema();
  const [schema, setSchema] = useState(tempSchema);
  const [orderTypes, setOrderTypes] = useState([]);
  const [isApproveShipment, setIsApproveShipment] = useState(null);
  const [isUniqueOrderNo, setIsUniqueOrderNo] = useState(false)
  const methods = useForm({
    mode: "onChange",
    defaultValues: getInitValues("payment", paymentData),
    resolver: yupResolver(schema),
  });
  const { reset, formState, setError, setValue } = methods;
  const { dirtyFields, isValid } = formState;
  const [dialogTitle, setDialogTitle] = useState('Order');
  const dialogParam = orderDialog?.param;
  const contetRef = useRef()
  const labelRef = useRef()
  const [loading, setLoading] = useState(false)
  const [selectedRevisionImages, setSelectedRevisionImages] = useState([])
  const [openContainersData , setOpenContainersData] = useState([]); // used to set the open containers list
  const [viewInvoiceDiscrepancy, setViewInvoiceDiscrepancy] = useState(null);
  const canViewInvoiceDiscrepancy = currentUser && FuseUtils.hasPermission(authPermissions.invoiceDiscrepancy, currentUser.role);
  const viewInvoiceId = paymentData?.id ?? paymentData?.invoice_id;

  /**
   * Initialize Dialog with Data
   */
  const initDialog = useCallback(() => {
    const currentSchemaName = isDispatchShipmentDialog() ? 'dispatch_shipment' : dialogParam.name
    const currentSchema = getSchema(currentSchemaName, orderDialog?.param);
    setSchema(currentSchema);
    if (orderDialog?.data) {
      if(orderDialog?.param?.isOutboundShipments && 
        orderDialog?.param?.isOutboundShipments === true && 
        isDialogType('approveActions') && ['Approve Shipment'].includes(orderDialog?.param?.title)) {
        setIsApproveShipment(true)
      } else setIsApproveShipment(null)
      let dialogName = isAdvancePaymentDialog() ? "advance_payment" : dialogParam.name;
      if(isDispatchShipmentDialog()) {
        dialogName = "dispatch_shipment";
      }
      let formSourceData = paymentData;
      if (dialogParam?.name === 'confirm_invoice' && completeOrderData) {
        // shipTo: defensive merge for legacy row/cache shapes; API uses snake_case ship_to.
        formSourceData = {
          ...paymentData,
          ship_to:
            paymentData?.ship_to ??
            paymentData?.shipTo ??
            completeOrderData?.ship_to ??
            completeOrderData?.shipTo,
          ship_to_rows: paymentData?.ship_to_rows ?? completeOrderData?.ship_to_rows,
          ship_to_multiple: paymentData?.ship_to_multiple ?? completeOrderData?.ship_to_multiple,
        };
      }
      const data = getInitValues(dialogName, formSourceData, orderDialog?.param);
      reset({ ...data });
      if (isDialogType('edit_order')) {
        dispatch(getOrderStatuses({ params: { c: 'order_type' } }))
          .unwrap()
          .then((res) => {
            setOrderTypes(res.data);
            const orderTypeName = data?.order_type?.name;
            if (orderTypeName) {
              const defaultValue = res.data.find(d => d.name === orderTypeName)
              if (defaultValue)
                setValue('order_type', defaultValue)
            }

          });
      }
    }
    if (dialogParam) {
      const title = dialogParam.title || `Update ${dialogParam.name}`;
      setDialogTitle(title);
    }
    if (isDialogType('payment') || isDialogType('complete_shipment')) {
      dispatch(
        getCategoryValues({
          params: { c: isDialogType('payment') ? 'payment_type' : 'shipping_company' },
        })
      )
        .unwrap()
        .then((response) => {
          const statusesTemp = response && response.data ? response.data : [];
          setOptions(statusesTemp);
        });
    }
    if (isDialogType('review_note')) {
      reset()
    }
    if(isDialogType('add_to_container')) {
      setOpenContainersData(orderDialog?.data);
    }
  }, [completeOrderData, dialogParam, dispatch, orderDialog?.data, reset]);

  const validateOrderNo = useCallback(
    // TODO Question - checkUnique is validationg email in user table. What if the user uses email that is in customer table not in user table? (customer login is not created)
    debounce((event, value) => {
      dispatch(checkUniqueOrderNo({ orderNo: value, id: orderDialog?.data?.id, }))
        .unwrap()
        .then((response) => {
          setIsUniqueOrderNo(Boolean(response.errors));
        });
    }, 500),
    [orderDialog]
  );

  /**
   * On Dialog Open
   */
  useEffect(() => {
    if (orderDialog?.props.open) {
      initDialog();
    }
  }, [orderDialog?.props.open, initDialog]);

  // View Invoice: keep discrepancy outside the printable invoice body (staff only).
  useEffect(() => {
    if (orderDialog?.props.open && dialogParam?.name === 'invoice' && canViewInvoiceDiscrepancy) {
      setViewInvoiceDiscrepancy(paymentData?.discrepancy || null);
    } else {
      setViewInvoiceDiscrepancy(null);
    }
  }, [orderDialog?.props.open, dialogParam?.name, paymentData?.discrepancy, canViewInvoiceDiscrepancy]);

  /**
   * Close Dialog
   */
  function closeDialog() {
    setLoading(false)
    setViewInvoiceDiscrepancy(null);
    if (orderDialog?.dashboard) {
      dispatch(
        closeViewOrderDialog({
          field: 'orderDialog',
        })
      );
    } else {
      dispatch(closeOrderDialog());
    }
    if (!dialogParam.keepOrderData) {
      dispatch(resetOrder())
    }
  }

  function isDialogType(name) {
    return dialogParam && dialogParam.name === name;
  }

  function isAdvancePaymentDialog() {
    return dialogParam && dialogParam.paymentType && dialogParam.name === 'payment' && dialogParam.paymentType === 'advance';
  }

  function isDispatchShipmentDialog() {
    return orderDialog && orderDialog?.param?.isDispatch && orderDialog?.param?.isDispatch === true;
  }

  function getSuccessMessage() {
    return dialogParam.successMessage
      ? dialogParam.successMessage
      : 'Order has been updated successfully';
  }

  // PGSS-5148: react-to-print v3 (contentRef) — v2 used findDOMNode, removed in React 19.
  const handleDownload = useReactToPrint({
    contentRef: contetRef,
    pageStyle: '',
    documentTitle: `invoice.pdf`,
  });

  const handleLabelDownload = useReactToPrint({
    contentRef: labelRef,
    pageStyle: '',
    documentTitle: `shipping-label.pdf`,
  });

  const handlePerformAction = (payload) => {
    dispatch(performAction(payload))
      .unwrap()
      .then(() => {
        if (dialogParam.loadOrdersFun) {
          dialogParam.loadOrdersFun();
        }
        dispatch(
          showMessage({
            message: getSuccessMessage(),
            type: 'success',
          })
        );
      })
      // PGSS-5613: a refused action used to fail silently here; show the backend's reason.
      .catch((errorBody) => {
        dispatch(showMessage({ type: 'error', message: getApiErrorMessage({ response: { data: errorBody } }, 'Failed to update') }));
      });
  }

  /**
   * handle on submit of Mark as Completed and Shipped With Duplicate Invoice number
   */
  const proceedWithDuplicateInvoice = (payload) => {
    // Set forced_update_invoice to true to accept  duplicate invoice number
    const payloadData = payload.data;
    payloadData.forced_update_invoice = true;
    handleCompleteShipmentSubmit(payload);
  }


  /**
   * handle on submit of Mark as Completed and Shipped
   */
  const handleCompleteShipmentSubmit = (payload) => {
    dispatch(performAction(payload))
      .unwrap()
      .then((res) => {
        // If Invoice Number already exists backend sends 200 renponse with Error object
        if (_.has(res, "Error")) {
          setLoading(false);
          // handled only Invoice Already exists response
          if (res.Error === "Invoice Already exists") {
            // Open Confirmation message to proceed with duplicate invoice number
            // If user confirms set forced_update_invoice flag
            // If user does not confirm stay in the same dialog
            dispatch(
              openDialog({
                children: (
                  <>
                    <DialogTitle id="alert-dialog-title">
                      Duplicate Invoice Number
                    </DialogTitle>
                    <DialogContent>
                      <DialogContentText id="alert-dialog-description">
                        Invoice number already exists. Do you want to proceed?
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={() => {
                          dispatch(fuseCloseDialog())
                        }}
                        color="primary"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          setLoading(true);
                          dispatch(fuseCloseDialog());
                          proceedWithDuplicateInvoice(payload);
                        }}
                        color="primary"
                        autoFocus
                      >
                        Confirm
                      </Button>
                    </DialogActions>
                  </>
                ),
              })
            );
          }
        }
        // PGSS-5790: boxes not scanned as loaded. Nothing was completed; the supplier scans them, or takes their
        // pieces out (back to In Production) and completes with the rest.
        if (res?.notLoaded === true) {
          setLoading(false);
          dispatch(
            openDialog({
              children: (
                <NotLoadedBoxesDialog
                  response={res}
                  onCancel={() => dispatch(fuseCloseDialog())}
                  onScan={() => {
                    dispatch(fuseCloseDialog());
                    closeDialog();
                    history.push(notLoadedScanPath(payload));
                  }}
                  onTakeOut={() => {
                    setLoading(true);
                    dispatch(fuseCloseDialog());
                    handleCompleteShipmentSubmit(withTakeOut(payload));
                  }}
                  // PGSS-5842: complete with the boxes left as they are, while the setting allows it.
                  onCompleteAnyway={() => {
                    setLoading(true);
                    dispatch(fuseCloseDialog());
                    handleCompleteShipmentSubmit(withCompleteAnyway(payload));
                  }}
                />
              ),
            })
          );
          return;
        }
        if (!_.has(res, "Error")) {
          // If response does not have error close dialog and proceed
          closeDialog();
          if (dialogParam.loadOrdersFun) {
            dialogParam.loadOrdersFun();
          }
          // PGSS-5562: the shipment completes either way, but its orders only move forward once
          // the container has been received. This dialog showed its own fixed "Completed And
          // Shipped" and threw the server's message away, so a hold looked identical to a success.
          const shown = completeShipmentMessage(res, getSuccessMessage());
          // PGSS-5790: after a take-out, repeat what came off, so it is pulled out of the container before it leaves.
          const takenOut = takenOutMessage(res);
          dispatch(showMessage(takenOut
            ? { message: res?.ordersHeld === true ? `${takenOut} ${res.message}` : takenOut, type: 'warning', autoHideDuration: null }
            : shown));
        }
      });
  }

  const includeRevisionDocuments = () => {
    if (['Request Quote Revision', 'Request Revision for Drawing', 'Request Revision for Vas price', 'Request Revision for Order'].includes(orderDialog?.param?.title) || orderDialog?.param?.showUploadRevisionDocuments === true) {
      return true
    }
    return false;
  }

  /**
   * Form Submit
   */
  function onSubmit(data) {
    const dataCopy = _.cloneDeep(data);
    if (isDialogType('invoice')) {
      handleDownload()
    }
    else if (isDialogType('internalNotes')) {
      closeDialog();
      return;
    }
    else if (isDialogType('shipping label')) {
      handleLabelDownload()
    }
    else if (isDialogType('rejectQuote')) {
      dialogParam?.onSubmit(data)
    }
    else if (isDialogType('archive_notes')) {
    // Only send the note text
    dialogParam?.onSubmit(data);
    closeDialog()
    return;
  } 
    else if (isDialogType('reasonNote')) {
      dialogParam?.onSubmit(data)
      closeDialog()
    }
    else if (isDialogType('address')) {
      if (data.address.id) {
        dispatch(updateAddress(data.address)).then(() => {
          dispatch(getOrderDetails({ id: orderDialog.data.id, params: {} }));
        });
      } else {
        const inputData = {
          id: data.cid,
          address_id: data.address_id,
          address: data.address,
          updateAddress: 'yes',
        };
        dispatch(updateCustomer(inputData)).then(() => {
          dispatch(getOrderDetails({ id: orderDialog.data.id, params: {} }));
        });
      }
    }
    else if (isDialogType('payment')) {
      if (isAdvancePaymentDialog()) {
        const payload = { ...data }
        delete payload.data;
        dispatch(addAdvancePayment(payload))
          .unwrap()
          .then(() => {
            // PGSS-5561: every other branch refreshes whatever opened it; this one did not, so a
            // payment added from the order's Advance Payments tab did not appear until a reload.
            if (dialogParam.loadOrdersFun) {
              dialogParam.loadOrdersFun();
            }
            dispatch(
              showMessage({
                message: 'Advance payment added successfully!',
                type: 'success',
              })
            );
          });
      } else {
        const payload = {
          ui_id: "MAKE_PAYMENT",
          data
        }
        delete payload.data?.data
        handlePerformAction(payload)
      }
    }
    else if (isDialogType('discount')) {
      const { discount, ...rest } = data
      const payload = {
        ui_id: "DISCOUNT",
        data: {
          order_ids: paymentData.order_ids,
          discount: +discount
        }
      }
      handlePerformAction(payload)
    }
    else if (isDialogType('confirm_invoice')) {
      // Only fields accepted by confirm-invoice API (display-only ship_to* omitted).
      const param = {
        shipping_cost: data.shipping_cost,
        tax: data.tax,
        comments_to_send: data.comments_to_send
      }
      dispatch(confirmInvoice({ id: data.id, param })).unwrap().then(() => {
        dialogParam.loadOrdersFun()
        dispatch(
          showMessage({
            message: getSuccessMessage(),
            variant: 'info',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right'
            },
          })
        );
      })
      // PGSS-5786: confirming a cancelled invoice can be refused (e.g. it has been replaced by a newer
      // invoice); show the backend's reason instead of failing silently.
      .catch((error) => {
        dispatch(showMessage({ type: 'error', message: getApiErrorMessage(error, 'Failed to confirm invoice') }));
      });
    }
    else if (isDialogType('update_invoice')) {
      const param = {
        shipping_cost: data.shipping_cost,
        tax: data.tax
      }
      dispatch(updateInvoice({ id: data.id, param })).unwrap().then(() => {
        dialogParam.loadOrdersFun()
        dispatch(
          showMessage({
            message: getSuccessMessage(),
            variant: 'info',
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'right'
            },
          })
        );
      })
    }
    else if (isDialogType('complete_shipment')) {
      setLoading(true);
      const payload = {
        ui_id: "COMPLETE_AND_SHIP",
        data: {
          ...data,
          packing_slips: data?.packing_slips && data?.packing_slips?.map((item) => ({ id: item.id })) || [],
          supplier_invoice: data?.supplier_invoice && data?.supplier_invoice?.map((item) => ({ id: item.id })) || []
        }
      }
      handleCompleteShipmentSubmit(payload)
    }
    else if (isDialogType('generate_invoice')) {
      const payload = {
        "ui_id": "GENERATE_INVOICE",
        data: {
          shipment_id: data.id,
          invoice_number: data.invoice_number
        }
      }
      handlePerformAction(payload)
    }
    else if (isDialogType('quick_edit_shipment')) {
      // Added condition to avoid backend error
      if (data && data.shipment_id && !checkNullOrUndefined(data.shipment_id)) {
        dispatch(editShipment(data)).unwrap().then((res) => {
          if (res?.message === 'Container Number exists') {
            setError('container_number', { message: res?.message })
          } else {
            const params = { stage: orderDialog.stage }
            if (dialogParam.loadOrdersFun) {
              dialogParam.loadOrdersFun();
            } else {
              dispatch(getOrders({ params }));
            }
            dispatch(
              showMessage({
                message: getSuccessMessage(),
                type: 'success',
              })
            );
            closeDialog()
          }
        });
      }
      return
    }
    else if (isDialogType('update_tracking')) {
      // PGSS-5407: carrier/tracking only — no status transition, so a shipment that has already been
      // dispatched keeps the status it is in while the PRO number is filled in or corrected.
      if (data && !checkNullOrUndefined(data.id)) {
        setLoading(true);
        dispatch(updateShipment({
          id: data.id,
          carrier: data.carrier || '',
          tracking_number: data.tracking_number || '',
        })).then((res) => {
          setLoading(false);
          if (!res?.error) {
            if (dialogParam.loadOrdersFun) {
              dialogParam.loadOrdersFun();
            }
            dispatch(
              showMessage({
                message: 'Tracking details have been updated successfully',
                type: 'success',
              })
            );
            closeDialog()
          }
        });
      }
      return
    }
    else if (isDialogType('editShipment')) {
      setLoading(true);
      if (data && data.id && !checkNullOrUndefined(data.id)) {
        data.to_statuses_type_id = dialogParam?.toStatusCode;
        const formatedDate = moment(data.schedule).format('YYYY-MM-DD');
        data.schedule = formatedDate;
        dispatch(updateShipment(data)).then((res) => {
          if(!res?.error) {
            setLoading(false);
            if (dialogParam.loadOrdersFun) {
              dialogParam.loadOrdersFun();
            }
            dispatch(
              showMessage({
                message: "Shipment has been updated successfully",
                type: 'success',
              })
            );
            closeDialog()
          }
        });
      }
      return
    }
    else if (isDialogType('approveActions') && dialogParam.returnToParentOnSubmit) {
      // Send selected_revision_documents to parent manually since selectedRevisionImages will be there in the form data
      if(dialogParam?.chooseRevisionDocuments && selectedRevisionImages && selectedRevisionImages.length > 0) {
        data.selected_revision_documents = selectedRevisionImages;
      }
      dialogParam?.onSubmit(data)
      closeDialog()
      setSelectedRevisionImages([]);
      return
    }
    else if (isDialogType('notesHistory')) {
      dispatch(addComments(data)).then((res) => {
        if(!res.error) {
          const submitMessage =  res.payload?.message ||  "Submitted successfully"
          dispatch(
            showMessage({
              message: submitMessage,
              variant: 'success',
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
            })
          );
        }
      })
      closeDialog()
      return
    }
    else {
      if (isDialogType('status')) {
        data.updateStatus = 'yes';
      }
      if (isDialogType('confirm')) {
        data.confirmOrder = 'yes';
      }
      if (isDialogType('note')) {
        data.updateNote = 'yes';
      }
      if (isDialogType('review_note')) {
        data.updateNote = 'yes';
        data.id = completeOrderData?.id
        data.note_list = [data?.note]
        if (data?.isInternalNotes) {
          data.note_list.push(`[INTERNAL] - ${data.internalNote}`)
        }
        if (dialogParam?.title !== 'Mark as Completed') {
          delete data?.note
        }
        delete data?.internalNote
        delete data?.isInternalNotes
      }
      if (isDialogType('approveActions')) {
        const isApproval = ['REJECT_DRAWING', 'REJECT_DRAWING_ORDER', 'APPROVE_DRAWING', 'APPROVE_DRAWING_ORDER',
          'SEND_FOR_DRAWING_APPROVAL', 'SEND_ORDER_FOR_DRAWING_APPROVAL', 'SEND_FOR_PROOF_APPROVAL', 'REQUEST_REVISION_FOR_PROOF', 'APPROVE_PROOF', 'REJECT', 'APPROVE_VAS_PRICE', 'SEND_PRICE_APPROVAL_FOR_VAS', 'REQUEST_REVISION_SUPPLIER_DRAWING'].includes(dialogParam?.data?.uiId)
        const isRejectQuote = ['REJECT', 'REJECT_DRAWING', 'REJECT_DRAWING_ORDER', 'REJECT_QUOTE', 'REQUEST_REVISION_FOR_VAS_PRICE', 'REQUEST_REVISION_FOR_DRAWING', 'REQUEST_REVISION_FOR_DRAWING_ORDER'].includes(dialogParam?.data?.uiId)

        if (isRejectQuote) {
          if (!data.note_type && !['Request Quote Revision', 'Request Revision for Vas price', 'Request Revision for Order', 'Request Revision for Drawing'].includes(orderDialog?.param?.title)) {
            setError('note_type', { message: 'Required' })
            return
          }
          // No need set Note error if dialogParam?.showAutoNote is passed. dialogParam?.showAutoNote handled seperately
          if (!data.note && !dialogParam?.showAutoNote && (data.note_type?.name === 'Others' || ['Request Quote Revision', 'Request Revision for Vas price', 'Request Revision for Drawing', 'Request Revision for Order'].includes(orderDialog?.param?.title))) {
            setError('note', { message: 'Required' })
            return
          }
        }
        // No need set Note error if dialogParam?.showAutoNote is passed. dialogParam?.showAutoNote handled seperately
        if (!data.note && !dialogParam?.showAutoNote && ['Request Revision for Drawing', 'Reject Shipment'].includes(orderDialog?.param?.title)) {
          setError('note', { message: 'Required' })
          return
        }
        if (!data.schedule && isDispatchShipmentDialog()) {
          setError('schedule', { message: 'Required' })
          return
        }

        const poData = {
          customer_po_number: data.customer_po_number,
          product_reference_no: data.product_reference_no
        };
        
        data = {
          id: data.id,
          note: data.note ? data.note : data.note_type?.name,
          to_statuses_type_id: dialogParam?.data?.toStatusCode,
          [isApproval ? 'update_status_with_attachment' : 'updateStatus']: 'yes'
        }
        
        if (['APPROVE_DRAWING'].includes(dialogParam?.data?.uiId) && isCustomer(currentUser.role) && orderDialog?.data?.is_import === true) {
          data.customer_po_number = poData.customer_po_number;
          data.product_reference_no = poData.product_reference_no;
        }       

        if (!data.note && (['REJECT_DRAWING', 'REJECT_DRAWING_ORDER', 'REJECT', 'REQUEST_REVISION_FOR_PROOF', 'REQUEST_REVISION_FOR_ORDER'].includes(dialogParam?.data?.uiId) || dialogParam?.data?.
          toStatusCode === "Send for Review")) {
          setError('note', { message: 'Required' })
          return
        }
        if(includeRevisionDocuments() && dataCopy?.new_revision_documents?.length > 0){
          data.new_revision_documents = dataCopy?.new_revision_documents;
        }
        if(dialogParam?.chooseRevisionDocuments && selectedRevisionImages && selectedRevisionImages.length > 0) {
          data.selected_revision_documents = selectedRevisionImages;
        }
        // Auto note field name can be anything like reject_note, revision_note.
        // Add to paload using dialogParam
        if (dialogParam?.showAutoNote && dialogParam?.noteFieldName) {
          data[dialogParam?.noteFieldName] = dataCopy[dialogParam?.noteFieldName];
        }
      }
      if (dialogParam.completeParam) {
        data.is_complete = true
        data.entity_history_id = dialogParam.entity_history_id
      }
      if (['Reject Shipment', 'Approve Shipment', 'Shipment Ready for Dispatch'].includes(orderDialog?.param?.title)) {
        // Added condition to avoid backend error undefined
        if (orderDialog && orderDialog?.data && !checkNullOrUndefined(orderDialog?.data?.shipment_id)) {
          setLoading(true);
          const isApproval = orderDialog?.param?.title === 'Approve Shipment';
          const shipmentParam = { id: orderDialog?.data?.shipment_id, note: data.note, is_approve: isApproval }
          if(isDispatchShipmentDialog()) {
            shipmentParam.schedule = dataCopy.schedule
            // Carrier + tracking number recorded as the shipment leaves the warehouse, so the
            // Tracking # column can link the customer straight to the carrier's tracking page.
            shipmentParam.carrier = dataCopy.carrier || ''
            shipmentParam.tracking_number = dataCopy.tracking_number || ''
            shipmentParam.to_statuses_type_id = orderDialog?.param?.toStatusCode;
            delete shipmentParam.is_approve;
          }
          dispatch(updateShipmentApproval(shipmentParam)).then(() => {
            setLoading(false);
            // the below lines close the pop up on API response 
            if(!checkNullOrUndefined(orderDialog?.param?.keepDialogOpen) && orderDialog?.param?.keepDialogOpen === false){
              closeDialog()
            }
            if (dialogParam.loadOrdersFun) {
                dialogParam.loadOrdersFun();
              }
            });
        }
      } else {
        if(isDialogType('edit_order')) {
          data.action_name = "quick_edit";
        }
        if(dialogParam?.data?.passExtraPayload === true && dialogParam?.data?.extraPayloadData) {
          data = {...data, ...dialogParam?.data?.extraPayloadData}
        }
        dispatch(updateOrder(data)).unwrap().then(() => {
          dispatch(
            showMessage({
              message: getSuccessMessage(),
              variant: 'info',
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
              },
            })
          );
          dispatch(newOrder());
          if (dialogParam.loadOrders === 'yes') {
            dispatch(getOrders());
          }
          if (dialogParam.loadOrdersFun) {
            dialogParam.loadOrdersFun();
          }
        })
        // PGSS-5338: the thunk now rejects when the backend refuses the edit (a committed supplier
        // order would desync). It has already shown the warning, so there is nothing to add here --
        // this catch exists so .unwrap() does not raise an unhandled rejection, and so the success
        // toast above cannot run for a save that did not happen.
        .catch(() => {});
      }
    }
    // this method prevents the pop p closure on submit
    if(!checkNullOrUndefined(orderDialog?.param?.keepDialogOpen) && !orderDialog?.param?.keepDialogOpen){
      return;
    }
    if (isDialogType('approveActions')) {
      if (orderDialog?.dashboard) {
        dispatch(closeViewOrderDialog())
      }
      else dispatch(orderCloseViewOrderDialog())
    }
    if (!isDialogType("complete_shipment")) {
      closeDialog();
    }
  }

  function disableSubmitButton() {
    if(!checkNullOrUndefined(orderDialog?.param?.keepDialogOpen) && !orderDialog?.param?.keepDialogOpen && isDialogType('approveActions')){
      return loading
    }
    if (isDialogType('invoice') || isDialogType('shipping label') || isDialogType('confirm') || isDialogType('approveActions') || isDialogType('review_note') || isDialogType('review_invoice_discrepancy')) return false
    if (isDialogType('complete_shipment')) {
      return loading || !!(_.isEmpty(dirtyFields) || !isValid)
    }
    return !!(_.isEmpty(dirtyFields) || !isValid);
  }

  function showDialogHeaderSection() {
    if (!checkNullOrUndefined(dialogParam?.showHeader)) return dialogParam?.showHeader;
    return !isDialogType('approveActions') && !isDialogType('view_notes') && !isDialogType('editShipment') && !isDialogType('update_tracking') && !isDialogType('reasonNote') && !isDialogType('notesHistory') && !isDialogType('revisionHistories');
  }

  const headerData = {
    enable: showDialogHeaderSection(),
    component: OrderHeader,
    data: {
      order: isDialogType('confirm_invoice') && paymentData ? { ...completeOrderData, ...paymentData } : completeOrderData,
      hideAmount: orderDialog.stage === 'invoice' || isAdvancePaymentDialog(),
      hideQuantity: orderDialog.stage === 'invoice' || isAdvancePaymentDialog(),
      // PGSS-5563: invoice stage hides Total Amount/Qty; show Invoice Amount / Paid / Due instead
      // (same as ViewOrderDialog). Without this the header only has Invoice No + Status.
      showInvoiceAmounts: orderDialog.stage === 'invoice' && !isSupplier(currentUser.role),
      hideCustomer: ['shipment', 'in-production'].includes(orderDialog.stage) || isSupplier(currentUser.role) && orderDialog.stage === 'invoice' || (orderDialog?.param?.name === 'payment' && (['customer', 'supplier'].includes(currentUser.role)) || (orderDialog.stage !== 'invoice') || (dialogParam?.isSupplierInvoice)) || orderDialog?.param?.hideCustomer,
      stage: orderDialog.stage,
      hideShipmentDetails: isDialogType('complete_shipment'),
      showTotalWeight: ['order', 'quote', 'approval', 'shipment', 'in-production'].includes(orderDialog.stage) && !isAdvancePaymentDialog(),
      showSupplierName: (orderDialog?.param?.name === 'payment' && dialogParam.isSupplierInvoice && !isAdvancePaymentDialog()) || orderDialog?.param?.showSupplierName,
      reviewNoteDialog: isDialogType('review_note'),
      showShipmentType: isDialogType('confirm_invoice'),
      confirmInvoiceShipTo: isDialogType('confirm_invoice'),
    },
  };

  const getSubmitButtonName = () => {
    if (dialogParam?.submitButtonName)
      return dialogParam.submitButtonName
    return isDialogType('invoice') ? 'Download' : ''
  }

  const getNoteExraParam = () => {
    if(isApproveShipment === true)
      return {'showShipmentType': true}
    if(isDispatchShipmentDialog() && isAdminOrStaff(currentUser))
      return {'isDispatchShiment': true}
    if (isCustomer(currentUser.role) && ['APPROVE_DRAWING'].includes(dialogParam?.data?.uiId) && orderDialog?.data?.is_import === true)
      return {'showPoReferenceNumberField': true};
    if (isDialogType("notesHistory")) {
      return {
        noteFieldName: orderDialog?.param?.noteFieldName || "note",
        noteFieldLabelName: orderDialog?.param?.noteFieldLabelName || "Note",
        addNote: !checkNullOrUndefined(orderDialog?.param?.addNote) ? orderDialog?.param?.addNote : true,
        showNoteHistory: !checkNullOrUndefined(orderDialog?.param?.showNoteHistory) ? orderDialog?.param?.showNoteHistory : true,
        notesData: orderDialog?.param?.notesData || [],
      };
    }
    if(['REQUEST_REVISION_SUPPLIER_DRAWING'].includes(dialogParam?.data?.uiId)) {
      return { showLatestRevisionNote: true, handleSelectedRevisionImages, selectedRevisionImages, showAutoNote: true }
    }
    if(dialogParam?.chooseRevisionDocuments === true) {
      return {handleSelectedRevisionImages, selectedRevisionImages}
    }
    return {};
  }

  /**
   * Handle check/uncheck revision documents in the "Make it To In-Review", "Request Drawing" popup
   */
  const handleSelectedRevisionImages = (media) => {
    setSelectedRevisionImages((prev) => {
      const exists = prev.some((item) => item.id === media.id);
      if (exists) {
        return prev.filter((item) => item.id !== media.id); // Deselect the image
      }
      return [...prev, media]; // Select the image
    });
  };

  // this method gets the last box number from the shipment
  function getStartOfNewBoxNumber(container, lastBoxNumber = 1) {
    if (container?.shipment_id) {
      return lastBoxNumber ?? 1;
    }
    return 1;
  }

  function onContainerListDialogClose() {
    closeDialog();
    if (dialogParam.loadOrdersFun) {
      dialogParam.loadOrdersFun();
    }
  }

  // this method is executed when container is selected for an order and passes the box number in the dialog
  const onContainerSelect = (container) => {
    const params = { orderId: orderDialog?.completeOrderData?.id };
    dispatch(getOrder(params)).then((response) => {
      let updatedOrder = response?.payload;
      if (updatedOrder?.order_details) {
        const enrichedDetails = _.map(updatedOrder.order_details, (detail) => ({
          ...detail,
          original_qty: detail.quantity,
        }));
        updatedOrder = {
          ...updatedOrder,
          order_detail: enrichedDetails,
        };
        delete updatedOrder.order_details;
      }
      const showSelectionBox = true;
      const shipmentId = container.shipment_id;
      let startOfNewNBoxNumber;
      dispatch(getLastBoxNumber({ shipmentId })).then((res) => {
        startOfNewNBoxNumber = getStartOfNewBoxNumber(
          container,
          res?.payload?.last_box_number
        );
        const isAddTocontainer = true;
        dispatch(
          openContainerDialog({
            data: updatedOrder,
            param: {
              name: "Container",
              title: "Update",
              successMessage: "Saved successfully",
              submitButtonName: "Update",
              maxWidth: "xl",
              showSelectionBox,
              startOfNewNBoxNumber,
              isAddTocontainer,
              onContainerListDialogClose,
              parent: "container-list",
              containerId: shipmentId,
              keepOrderData: true
            },
          })
        );
      });
    });
  };

  return orderDialog && orderDialog.props && orderDialog.props.open ? (
    <>
      <FormProvider {...methods}>
        <AuraBaseDialog
          dialogProps={{ open: orderDialog.props.open, fullScreen: isDialogType('invoice') }}
          closeDialog={closeDialog}
          dialogParam={{
            title: dialogTitle,
            submitButtonName: getSubmitButtonName(),
            maxWidth: dialogParam.maxWidth
              || ((isDialogType('confirm_invoice')
                || isDialogType('update_invoice')
                || isDialogType('review_invoice_discrepancy'))
                && paymentData?.discrepancy?.warning_active
                ? 'md'
                : undefined),
            hideDialogActions: dialogParam?.hideDialogActions
          }}
          onSubmit={onSubmit}
          disableSubmitButton={disableSubmitButton}
          header={headerData}
          additionalButton={dialogParam?.additionalButton}
          disableCloseIcon={loading}
          paperClass=''
        >
          {loading ? (<Loader />) : (
            <>
              {isDialogType('status') && <StatusFormContent statuses={updateStatusValues} />}
              {(isDialogType('note') || isDialogType('review_note') || isDialogType('confirm') || isDialogType('rejectQuote') || isDialogType('approveActions') || isDialogType('reasonNote') || isDialogType('notesHistory')|| isDialogType('archive_notes')) && (
                <NoteFormContent confirmOrder={isDialogType('confirm')} title={orderDialog?.param?.title} uiId={dialogParam?.data?.uiId} orderDialog={orderDialog} extraParam={getNoteExraParam()} />
              )}
              {isDialogType('address') && <AddressFormContent addressLine1Required />}
              {isDialogType('payment') && <PaymentFormContent paymentTypes={options} isAdvancePayment={isAdvancePaymentDialog()} isSupplierInvoice={dialogParam?.isSupplierInvoice} />}
              {isDialogType('invoice') && (
                <>
                  {canViewInvoiceDiscrepancy && viewInvoiceId && viewInvoiceDiscrepancy && (
                    <div className="print:hidden mb-16 px-16 md:px-24">
                      <InvoiceDiscrepancyPanel
                        invoiceId={viewInvoiceId}
                        discrepancy={viewInvoiceDiscrepancy}
                        compact
                        readOnly
                      />
                    </div>
                  )}
                  <ComponentToPrint
                    ref={contetRef}
                    type="dialog"
                    id={viewInvoiceId}
                    hideDiscrepancy
                  />
                </>
              )}
              {isDialogType('discount') && <DiscountFormContent type='dialog' />}
              {isDialogType('quick_edit_shipment') && <QuickEditShipmentForm type='dialog' />}
              {isDialogType('shipping label') && <PrintShippingLabelContent ref={labelRef} data={orderDialog.param.data} />}
              {isDialogType('complete_shipment') && <CompleteShipmentFormContent options={options} />}
              {isDialogType('confirm_invoice') && <ConfirmInvoice isDashboard={props.isDashboard} />}
              {isDialogType('update_invoice') && <UpdateInvoice isDashboard={props.isDashboard} />}
              {isDialogType('review_invoice_discrepancy') && (
                <ReviewInvoiceDiscrepancy isDashboard={props.isDashboard} />
              )}
              {isDialogType('generate_invoice') && <GenerateInvoice />}
              {isDialogType('edit_order') && <QuickOrderEdit validateOrderNo={validateOrderNo} isUniqueOrderNo={isUniqueOrderNo} orderTypes={orderTypes} data={orderDialog?.data} extraParam={{stage:orderDialog.stage}} />}
              {isDialogType('view_notes') && <ViewNotes paramData={orderDialog.param} />}
              {isDialogType('internalNotes') && <InternalNotes paramData={orderDialog.param} />}
              {isDialogType('editShipment') && <ShipmentFormContent type="edit" />}
              {isDialogType('update_tracking') && <UpdateTrackingFormContent />}
              {isDialogType('revisionHistories') &&
                <RevisionHistories
                  updateRecords={orderDialog.param.updateRecords ?? true}
                  productRevisionData={orderDialog.param.productRevisionData ?? []}
                  vasRevisionData={orderDialog.param.vasRevisionData ?? []}
                  order={orderDialog?.data}
                  user={currentUser}
                  showProductRevision={
                    orderDialog.param.showProductRevision ?? true
                  }
                  accordianStyle={orderDialog.param.accordianStyle ?? true}
                />}
              {isDialogType('add_to_container') &&
                <OpenContainersContent data = {openContainersData} onContainerSelect = {onContainerSelect}/>
              }
            </>
          )}
        </AuraBaseDialog>
        <ContainerDialog/>
      </FormProvider>
    </>
  ) : (
    <></>
  );
}

export default OrderDialog;